import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  sanitizeInput,
  validateDate,
  validateTime,
} from "../../utils/inputValidation";
import { generateInvitationCodes } from "../../utils/invitationCodeGenerator";

interface EventManagerProps {
  mode: "create" | "edit";
  eventId?: string;
  onSuccess?: () => void;
}

const EventManager: React.FC<EventManagerProps> = ({
  mode,
  eventId,
  onSuccess,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [address, setAddress] = useState("");
  const [maxTickets, setMaxTickets] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(mode === "edit");
  
  // New fields for closed events
  const [isClosedEvent, setIsClosedEvent] = useState(false);
  const [numberOfCodes, setNumberOfCodes] = useState("");
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);

  useEffect(() => {
    if (mode === "edit" && eventId) {
      const fetchEvent = async () => {
        try {
          const eventDoc = await getDoc(doc(db, "events", eventId));
          if (eventDoc.exists()) {
            const data = eventDoc.data();
            setTitle(data.title || "");
            setDescription(data.description || "");
            setDate(data.date || "");
            setTime(data.time || "");
            setAddress(data.address || "");
            setMaxTickets(data.maxTickets?.toString() || "");
            setIsClosedEvent(data.isClosedEvent || false);
            
            // If editing a closed event, fetch the current codes
            if (data.isClosedEvent) {
              try {
                const codesDoc = await getDoc(doc(db, `events/${eventId}/metadata/invitationCodes`));
                if (codesDoc.exists()) {
                  const codesData = codesDoc.data();
                  setGeneratedCodes(codesData.availableCodes || []);
                  setNumberOfCodes(codesData.availableCodes?.length.toString() || "0");
                }
              } catch (error) {
                console.error("Error fetching invitation codes:", error);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching event:", error);
          setError("Failed to load event details");
        } finally {
          setLoading(false);
        }
      };
      fetchEvent();
    }
  }, [mode, eventId]);

  const handleGenerateCodes = () => {
    const numCodes = parseInt(numberOfCodes, 10);
    if (isNaN(numCodes) || numCodes <= 0) {
      setError("Please enter a valid number of invitation codes.");
      return;
    }
    
    // Generate unique invitation codes
    const codes = generateInvitationCodes(numCodes);
    setGeneratedCodes(codes);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const sanitizedTitle = sanitizeInput(title);
    const sanitizedAddress = sanitizeInput(address);
    const sanitizedMaxTickets = sanitizeInput(maxTickets);

    // Validations
    if (!sanitizedTitle.trim()) {
      setError("Event title is required.");
      return;
    }

    if (!validateDate(date)) {
      setError("Please select a future date.");
      return;
    }

    if (!validateTime(time)) {
      setError("Please enter a valid time in HH:MM format.");
      return;
    }

    if (!sanitizedAddress.trim()) {
      setError("Event address is required.");
      return;
    }

    const parsedMaxTickets = parseInt(sanitizedMaxTickets, 10);
    if (isNaN(parsedMaxTickets) || parsedMaxTickets <= 0) {
      setError("Please enter a valid number of maximum tickets.");
      return;
    }

    // Validate closed event requirements
    if (isClosedEvent && generatedCodes.length === 0) {
      setError("Please generate invitation codes for your closed event.");
      return;
    }

    try {
      const eventData = {
        title: sanitizedTitle,
        description,
        date,
        time,
        address: sanitizedAddress,
        maxTickets: parsedMaxTickets,
        isClosedEvent,
        updatedAt: Timestamp.now(),
        ...(mode === "create" && { createdAt: Timestamp.now() }),
      };

      const docId = mode === "edit" ? eventId : generateEventId(sanitizedTitle);
      await setDoc(doc(db, "events", docId!), eventData, { merge: true });

      // Save invitation codes for closed events
      if (isClosedEvent) {
        await setDoc(
          doc(db, `events/${docId}/metadata/invitationCodes`),
          {
            availableCodes: generatedCodes,
            totalCodes: generatedCodes.length,
            updatedAt: Timestamp.now()
          }
        );
      }

      if (onSuccess) onSuccess();

      alert(`Event ${mode === "create" ? "created" : "updated"} successfully!`);
    } catch (error) {
      console.error(`Error ${mode}ing event:`, error);
      setError(
        `An error occurred while ${mode}ing the event. Please try again.`
      );
    }
  };

  const generateEventId = (title: string): string => {
    return `${title.toLowerCase().replace(/[^a-z0-9]/g, "-")}-${Date.now()}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-primary text-white p-6">
        <h2 className="text-2xl font-bold">
          {mode === "create" ? "Create New Event" : "Edit Event"}
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div>
          <label
            htmlFor="eventTitle"
            className="block text-sm font-medium text-gray-700"
          >
            Event Title
          </label>
          <input
            type="text"
            id="eventTitle"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="eventDescription"
            className="block text-sm font-medium text-gray-700"
          >
            Event Description
          </label>
          <div className="mt-1 mb-2 text-sm text-gray-500">
            Use markdown formatting:
            <ul className="list-disc list-inside">
              <li>*italic* for italic text</li>
              <li>**bold** for bold text</li>
              <li>- item for bullet points</li>
            </ul>
          </div>
          <textarea
            id="eventDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={10}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="eventDate"
              className="block text-sm font-medium text-gray-700"
            >
              Event Date
            </label>
            <input
              type="date"
              id="eventDate"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            />
          </div>

          <div>
            <label
              htmlFor="eventTime"
              className="block text-sm font-medium text-gray-700"
            >
              Event Time
            </label>
            <input
              type="time"
              id="eventTime"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="eventAddress"
            className="block text-sm font-medium text-gray-700"
          >
            Event Address
          </label>
          <textarea
            id="eventAddress"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            placeholder="Enter the full address of the event"
          />
        </div>

        <div>
          <label
            htmlFor="maxTickets"
            className="block text-sm font-medium text-gray-700"
          >
            Maximum Tickets
          </label>
          <input
            type="number"
            id="maxTickets"
            value={maxTickets}
            onChange={(e) => setMaxTickets(e.target.value)}
            required
            min="1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
          />
        </div>

        {/* Closed event toggle */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isClosedEvent"
            checked={isClosedEvent}
            onChange={(e) => setIsClosedEvent(e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label 
            htmlFor="isClosedEvent" 
            className="ml-2 block text-sm text-gray-700"
          >
            This is a private event (invitation codes required)
          </label>
        </div>

        {/* Invitation codes generator (only shows when closed event is selected) */}
        {isClosedEvent && (
          <div className="p-4 border border-gray-300 rounded-md bg-gray-50">
            <h3 className="font-medium text-gray-700 mb-2">Invitation Codes</h3>
            
            <div className="flex space-x-2 mb-4">
              <div className="flex-1">
                <label htmlFor="numberOfCodes" className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Codes
                </label>
                <input
                  type="number"
                  id="numberOfCodes"
                  value={numberOfCodes}
                  onChange={(e) => setNumberOfCodes(e.target.value)}
                  min="1"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  placeholder="How many codes to generate"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleGenerateCodes}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                >
                  Generate
                </button>
              </div>
            </div>

            {generatedCodes.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Generated Codes:</span>
                  <span className="text-xs text-gray-500">{generatedCodes.length} codes</span>
                </div>
                <div className="bg-white p-2 rounded border border-gray-300 max-h-40 overflow-y-auto">
                  <ul className="text-xs text-gray-600 grid grid-cols-2 md:grid-cols-3 gap-1">
                    {generatedCodes.map((code, index) => (
                      <li key={index} className="font-mono">{code}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 flex justify-end space-x-4">
          {mode === "edit" && (
            <button
              type="button"
              onClick={() => onSuccess?.()}
              className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            {mode === "create" ? "Create Event" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventManager;